
import { useQuery } from "@tanstack/react-query";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AnalyticsOverview } from "@/components/analytics/AnalyticsOverview";
import { RecipeActivityChart } from "@/components/analytics/RecipeActivityChart";
import { RecipeCategoryChart } from "@/components/analytics/RecipeCategoryChart";
import { AdditionalStats } from "@/components/analytics/AdditionalStats";
import { useAnalyticsData } from "@/hooks/useAnalyticsData";
import { supabase, getValidSession, isCreator } from "@/lib/supabase";
import { toast } from "sonner";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { subMonths } from "date-fns";

interface ActivityData {
  created_at: string;
  likes_count: number;
  earnings: number;
  activity_type: string;
}

const Analytics = () => {
  const navigate = useNavigate();
  const { data: analytics, isLoading: analyticsLoading, error: analyticsError } = useAnalyticsData();

  useEffect(() => {
    const checkAuth = async () => {
      const session = await getValidSession();
      
      if (!session) {
        toast.error("Please log in to view analytics");
        navigate("/login");
        return;
      }

      const isUserCreator = await isCreator(session.user.id);
      if (!isUserCreator) {
        toast.error("Analytics are only available for creators");
        navigate("/dashboard");
        return;
      }
    };

    checkAuth();
  }, [navigate]);

  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ["recipe-activity"],
    queryFn: async () => {
      const session = await getValidSession();
      if (!session) {
        throw new Error("Not authenticated");
      }

      // Get data for the last year to ensure we have enough data for all time ranges
      const startDate = subMonths(new Date(), 12);

      const { data, error } = await supabase
        .from('recipe_activity')
        .select('created_at, likes_count, earnings, activity_type') // Added activity_type to select
        .eq('user_id', session.user.id)
        .in('activity_type', ['like', 'earnings'])
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching activity data:', error);
        throw error;
      }

      // Create a map to aggregate data by date
      const dateMap = new Map();
      
      (data as ActivityData[]).forEach(item => {
        const date = item.created_at.split('T')[0]; // Get just the date part
        const existing = dateMap.get(date) || { date, likes: 0, earnings: 0 };
        
        if (item.activity_type === 'like') {
          existing.likes = (existing.likes || 0) + (item.likes_count || 0);
        } else if (item.activity_type === 'earnings') {
          existing.earnings = (existing.earnings || 0) + (item.earnings || 0);
        }
        
        dateMap.set(date, existing);
      });

      // Convert map to array and sort by date
      const formattedData = Array.from(dateMap.values())
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      return formattedData;
    },
    enabled: !!analytics,
    retry: false,
    staleTime: 30000 // Cache for 30 seconds
  });

  if (analyticsError) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full">
          <DashboardSidebar />
          <div className="flex-1 flex flex-col">
            <Header />
            <main className="flex-1 container max-w-7xl mx-auto px-4 py-8">
              <div className="text-center py-12">
                <p className="text-red-500">Failed to load analytics data. Please try again later.</p>
              </div>
            </main>
            <Footer />
          </div>
        </div>
      </SidebarProvider>
    );
  }

  const categoryData = analytics ? Object.entries(analytics.recipes_by_category || {})
    .filter(([name, value]) => value > 0)
    .map(([name, value]) => ({
      name,
      value,
    }))
    .sort((a, b) => b.value - a.value)
    : [];

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <DashboardSidebar />
        <div className="flex-1 flex flex-col">
          <Header />
          <main className="flex-1 container max-w-7xl mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold mb-6">Analytics Dashboard</h1>
            
            <AnalyticsOverview 
              analytics={analytics} 
              isLoading={analyticsLoading} 
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {!activityLoading && (
                <RecipeActivityChart data={activityData || []} />
              )}
              {!analyticsLoading && (
                <RecipeCategoryChart data={categoryData} />
              )}
            </div>

            <div className="grid grid-cols-1 gap-6">
              {analytics && (
                <AdditionalStats
                  favoriteRecipes={analytics.favorite_recipes}
                  totalCookingTime={analytics.total_cooking_time}
                  mostUsedIngredient={analytics.most_used_ingredient}
                  totalCalories={analytics.total_calories}
                  fastestRecipe={analytics.fastest_recipe}
                  longestRecipe={analytics.longest_recipe}
                />
              )}
            </div>
          </main>
          <Footer />
        </div>
      </div>
    </SidebarProvider>
  );
};

export default Analytics;
